import { Component, ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { LoginModalComponent } from '../login-modal/login-modal.component';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../services/auth.service';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { DepositModalComponent } from '../deposit-modal/deposit-modal.component';
import { ModalHelperComponent } from '../modal-helper/modal-helper.component';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { ToastService } from '../../../services/toast.service';
import { Web3ServiceService } from '../../../services/web3-service.service';
import { ApiService } from '../../../services/api.service';
import { EnableModalComponent } from '../enable-modal/enable-modal.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    LoginModalComponent,
    RouterModule,
    DepositModalComponent,
    ModalHelperComponent,
    ClipboardModule,
    EnableModalComponent
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css'
})
export class HeaderComponent implements OnInit, OnDestroy {
  modalOpen = false;
  dropdownOpen = false;
  private dropdownCooldown = false;
  depositModalOpen = false;
  isTransferCryptoOpen = false;
  userData: any;
  usdcBalance: any = 0;
  portfolioValue: any = 0;
  enableTradingModalOpen = false;
  enableProfileFlowModalOpen = false;
  private portfolioGlobalSubscription: Subscription | undefined;
  private activeStreamUserId: string | null = null;

  get isVerifyEmailPage(): boolean {
    const isVerify = this.router.url.includes('/verify-email') || this.router.url.includes('/callback');
    // console.log(isVerify, 'isVerifyEmailPage');
    return isVerify;
  }

  constructor(
    private router: Router,
    public auth: AuthService,
    private toast: ToastService,
    private api: ApiService,
    private web3Service: Web3ServiceService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.userData = this.auth.getUserData()
    this.auth.operation
      .subscribe((name: string) => {
        if (name == "login") {
          this.openModal();
          this.cdr.detectChanges();
        } else if (name == "enable-trading") {
          this.openEnableFlow();
          this.cdr.detectChanges();
        }
      });

    this.auth.isConnected$
      .subscribe((result: boolean) => {
        if (result === true) {
          this.userData = this.auth.getUserData();
          this.dropdownOpen = false;
          this.dropdownCooldown = true;
          setTimeout(() => this.dropdownCooldown = false, 500);

          // Only fetch profile if AppComponent hasn't already done it (e.g. after a fresh login)
          if (this.userData?.id && !this.auth.isInitialProfileLoaded) {
            this.getProfileData(this.userData.id);
          }
        } else if (result === false) {
          this.userData = {};
          this.usdcBalance = 0;
          this.portfolioValue = 0;
          this.dropdownOpen = false;
          this.cdr.detectChanges();
        }
      });

    // Subscribe to the global stream managed by AuthService
    this.portfolioGlobalSubscription = this.auth.portfolioSummary$.subscribe({
      next: (res: any) => {
        if (!res) return;
        // console.log("Header UI Update from Global Stream:", res);
        // Map the fields from the streaming response
        this.usdcBalance = res.cash_balance ?? res.data?.cash_balance ?? this.usdcBalance;
        this.portfolioValue = res.portfolio_value ?? res.data?.portfolio_value ?? this.portfolioValue;
        this.cdr.detectChanges();
      }
    });
    // Close dropdown on any route change
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.dropdownOpen = false;
      }
    });

  }

  ngOnDestroy() {
    if (this.portfolioGlobalSubscription) {
      this.portfolioGlobalSubscription.unsubscribe();
    }
  }

  // Local stream removed - now using global stream from AuthService

  /**
   * Get user profile
   * @param userId 
   */
  getProfileData(userId: any) {
    if (!userId) return;
    this.api
      .getUserProfile(userId)
      .subscribe(async (result: any) => {
        this.userData = result.data;
        this.cdr.detectChanges();
      });
  }

  openDropdown() {
    if (this.dropdownCooldown) return;
    this.dropdownOpen = true;
  }

  navigateToPortfolio() {
    this.router.navigate(['/portfolio']);
  }

  closeDropdown() {
    this.dropdownOpen = false;
  }

  openModal() {
    this.modalOpen = true;
  }

  depositModal() {
    if (!this.auth.isLoggedIn()) {
      this.auth.sendOperationSignal("login");
      return;
    }

    if (!this.auth.isTradeEnabled()) {
      this.enableTradingModalOpen = true;
      return;
    }
    this.depositModalOpen = true;
  }

  closeDepositModal() {
    this.depositModalOpen = false;
  }

  closeEnableTradingModal() {
    this.enableTradingModalOpen = false;
  }

  closeEnableProfileFlowModal() {
    this.enableProfileFlowModalOpen = false;
    // Show enable-trading modal only after profile flow for new users.
    this.enableTradingModalOpen = true;
  }

  private openEnableFlow(): void {
    this.dropdownOpen = false;
    const rawIsExistingUser = this.userData?.is_existing_user;

    const isExistingUser = typeof rawIsExistingUser === 'string'
      ? rawIsExistingUser.toLowerCase() === 'true'
      : rawIsExistingUser === true;

    if (isExistingUser === false) {
      this.enableProfileFlowModalOpen = true;
      this.enableTradingModalOpen = false;
      return;
    }

    this.enableTradingModalOpen = true;
    this.enableProfileFlowModalOpen = false;
  }

  copyText(): void {
    navigator
      .clipboard
      .writeText(this.userData.proxy_wallet)
      .then(() => {
        this.toast.success("Address copied successfully");
      })
  }
}
